import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { Activity, AlertCircle, AlertTriangle, Baby, BarChart3, BookOpen, Brain, Calendar, CheckCircle, ChevronDown, ChevronUp, ClipboardList, Clock, Eye, FileText, Heart, Home, Lightbulb, MessageCircle, Phone, RefreshCw, Shield, Star, TrendingDown, TrendingUp, User, UserCheck } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalRedFlags = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const res = await axios.get(`${BACKEND_URL}/api/principal/red-flags?token=${token}`);
      setAlerts(res.data.alerts || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = async (alertId) => {
    if (!resolutionNote.trim()) {
      alert('Please add a resolution note');
      return;
    }
    
    setResolving(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(
        `${BACKEND_URL}/api/principal/red-flags/${alertId}/resolve`,
        null,
        { params: { token, resolution_notes: resolutionNote } }
      );
      
      setResolutionNote('');
      setExpandedAlert(null);
      loadData();
    } catch (error) {
      console.error('Error resolving alert:', error);
      alert('Failed to resolve alert');
    } finally {
      setResolving(false);
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'red':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          icon: AlertTriangle,
          iconColor: 'text-red-600',
          badgeBg: 'bg-red-100',
          badgeText: 'text-red-700',
          label: 'Critical'
        };
      case 'orange':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-400',
          icon: AlertCircle,
          iconColor: 'text-orange-600',
          badgeBg: 'bg-orange-100',
          badgeText: 'text-orange-700',
          label: 'Warning'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-400',
          icon: AlertCircle,
          iconColor: 'text-yellow-600',
          badgeBg: 'bg-yellow-100',
          badgeText: 'text-yellow-700',
          label: 'Attention'
        };
      default:
        return {
          bg: 'bg-slate-50',
          border: 'border-slate-400',
          icon: AlertCircle,
          iconColor: 'text-slate-600',
          badgeBg: 'bg-slate-100',
          badgeText: 'text-slate-700',
          label: 'Info'
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodTrendIcon = (trend) => {
    if (trend?.includes('declining') || trend?.includes('anxious')) {
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    } else if (trend?.includes('positive')) {
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    }
    return <Activity className="w-4 h-4 text-yellow-500" />;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'active') return alert.status !== 'resolved';
    if (filter === 'resolved') return alert.status === 'resolved';
    return alert.severity === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="red-flags-page">
      
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Shield className="w-7 h-7 text-red-500" />
                Red Flag Dashboard
              </h1>
              <p className="text-slate-600">Monitor and respond to child development alerts</p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Critical</p>
                    <p className="text-2xl font-bold text-red-600">{summary?.critical || 0}</p>
                  </div>
                  <AlertTriangle className="w-10 h-10 text-red-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Active Alerts</p>
                    <p className="text-2xl font-bold text-orange-600">{summary?.active || 0}</p>
                  </div>
                  <AlertCircle className="w-10 h-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Resolved</p>
                    <p className="text-2xl font-bold text-green-600">
                      {alerts.filter(a => a.status === 'resolved').length}
                    </p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Children Monitored</p>
                    <p className="text-2xl font-bold text-sanjaya-navy">{summary?.total_children || 0}</p>
                  </div>
                  <Baby className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {['all', 'active', 'red', 'orange', 'yellow', 'resolved'].map(f => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                onClick={() => setFilter(f)}
                size="sm"
                className={filter === f ? (
                  f === 'red' ? 'bg-red-600' : 
                  f === 'orange' ? 'bg-orange-600' : 
                  f === 'yellow' ? 'bg-yellow-600' : 
                  f === 'resolved' ? 'bg-green-600' : ''
                ) : ''}
              >
                {f === 'all' ? `All (${alerts.length})` : 
                 f === 'active' ? 'Active' :
                 f === 'red' ? 'Critical' :
                 f === 'orange' ? 'Warning' :
                 f === 'yellow' ? 'Attention' : 'Resolved'}
              </Button>
            ))}
          </div>

          {/* Alerts List */}
          {filteredAlerts.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">All Clear!</h3>
                <p className="text-slate-600">No alerts match your current filter.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredAlerts.map((alert) => {
                const config = getSeverityConfig(alert.severity);
                const Icon = config.icon;
                const isExpanded = expandedAlert === alert.id;
                const isResolved = alert.status === 'resolved';
                const profile = alert.child_profile_summary || {};
                
                return (
                  <Card 
                    key={alert.id}
                    className={`${config.border} border-l-4 ${isResolved ? 'opacity-70' : ''}`}
                  >
                    <CardContent className="p-0">
                      {/* Alert Header */}
                      <div 
                        className={`p-5 cursor-pointer ${config.bg}`}
                        onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-full bg-white shadow-sm`}>
                              <Icon className={`w-6 h-6 ${config.iconColor}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1 flex-wrap">
                                <h3 className="font-bold text-slate-800 text-lg">{alert.child_name}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.badgeBg} ${config.badgeText}`}>
                                  {config.label}
                                </span>
                                {isResolved && (
                                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                    ✓ Resolved
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-800 font-semibold">{alert.category}</p>
                              <p className="text-slate-600 mt-1">{alert.description}</p>
                              <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(alert.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {alert.observer_name || 'Observer'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button className="p-2 hover:bg-white/50 rounded-full">
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="p-5 border-t bg-white">
                          <div className="grid md:grid-cols-2 gap-6">
                            
                            {/* Left Column - Child Profile & Indicators */}
                            <div className="space-y-5">
                              {/* Child Profile Summary */}
                              <div className="bg-blue-50 p-4 rounded-xl">
                                <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                  <Baby className="w-5 h-5 text-sanjaya-navy" />
                                  Child Profile
                                </h4>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-slate-500">Age:</span>
                                    <span className="ml-2 font-semibold">{profile.age || 'N/A'} years</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Sessions:</span>
                                    <span className="ml-2 font-semibold">{profile.total_sessions || 0}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-slate-500">Engagement:</span>
                                    <span className="ml-2 font-semibold flex items-center gap-1">
                                      <Star className="w-4 h-4 text-yellow-500" fill="#eab308" />
                                      {profile.avg_engagement_score || 'N/A'}/10
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-slate-500">Mood Trend:</span>
                                    <span className="ml-2 font-semibold flex items-center gap-1">
                                      {getMoodTrendIcon(profile.recent_mood_trend)}
                                      {profile.recent_mood_trend || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                                {profile.key_interests && (
                                  <div className="mt-3">
                                    <span className="text-slate-500 text-sm">Interests:</span>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {profile.key_interests.map((interest, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-white rounded-full text-xs text-blue-700">
                                          {interest}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {profile.communication_style && (
                                  <p className="mt-3 text-sm text-slate-600">
                                    <MessageCircle className="w-4 h-4 inline mr-1" />
                                    {profile.communication_style}
                                  </p>
                                )}
                              </div>

                              {/* Key Indicators */}
                              {alert.indicators && alert.indicators.length > 0 && (
                                <div>
                                  <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-orange-600" />
                                    Key Indicators Observed
                                  </h4>
                                  <ul className="space-y-2">
                                    {alert.indicators.map((indicator, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-sm">
                                        <span className={`w-2 h-2 rounded-full mt-1.5 ${config.iconColor.replace('text-', 'bg-')}`}></span>
                                        <span className="text-slate-700">{indicator}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Session Context */}
                              {alert.session_context && (
                                <div className="bg-slate-50 p-4 rounded-xl">
                                  <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                                    <BookOpen className="w-5 h-5 text-slate-600" />
                                    Session Context
                                  </h4>
                                  <p className="text-sm text-slate-700 italic">"{alert.session_context}"</p>
                                </div>
                              )}
                            </div>

                            {/* Right Column - Actions & Resolution */}
                            <div className="space-y-5">
                              {/* Recommended Actions */}
                              {alert.recommended_actions && alert.recommended_actions.length > 0 && (
                                <div className="bg-amber-50 p-4 rounded-xl">
                                  <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                    <Lightbulb className="w-5 h-5 text-amber-600" />
                                    Recommended Actions
                                  </h4>
                                  <ul className="space-y-2">
                                    {alert.recommended_actions.map((action, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-sm">
                                        <span className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center text-amber-800 font-bold text-xs flex-shrink-0">
                                          {idx + 1}
                                        </span>
                                        <span className="text-slate-700">{action}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Quick Actions */}
                              {!isResolved && (
                                <div>
                                  <h4 className="font-bold text-slate-800 mb-3">Quick Actions</h4>
                                  <div className="flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm">
                                      <Phone className="w-4 h-4 mr-2" />
                                      Contact Parent
                                    </Button>
                                    <Button variant="outline" size="sm">
                                      <MessageCircle className="w-4 h-4 mr-2" />
                                      Message Observer
                                    </Button>
                                    <Button variant="outline" size="sm">
                                      <FileText className="w-4 h-4 mr-2" />
                                      View Full Profile
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {/* Resolution Section */}
                              {!isResolved ? (
                                <div className="bg-slate-100 p-4 rounded-xl">
                                  <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Resolve This Alert
                                  </h4>
                                  <Textarea
                                    value={expandedAlert === alert.id ? resolutionNote : ''}
                                    onChange={(e) => setResolutionNote(e.target.value)}
                                    placeholder="Document your resolution actions and notes..."
                                    className="mb-3 bg-white"
                                    rows={3}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setExpandedAlert(null);
                                        setResolutionNote('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => resolveAlert(alert.id)}
                                      disabled={resolving || !resolutionNote.trim()}
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                    >
                                      {resolving ? 'Saving...' : 'Mark Resolved'}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                                  <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5" />
                                    Resolution Notes
                                  </h4>
                                  <p className="text-green-700">{alert.resolution_notes || 'No notes provided'}</p>
                                  <p className="text-sm text-green-600 mt-2">
                                    Resolved: {formatDate(alert.resolved_at)}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrincipalRedFlags;
